/**
 * reponsesDeLEditeur — lire ce que le Document API rend, et rédiger les deux
 * refus que tout verbe hors du corps partage.
 *
 * Trois exécuteurs lisent les mêmes enveloppes (`{ items }`, `{ success,
 * failure }`, l'adresse d'une section) et refusent dans les mêmes mots quand
 * une surface manque : `footnoteActionExecutors.ts`,
 * `headerFooterActionExecutors.ts`, `pageLayoutExecutor.ts`. Chacun en portait
 * sa copie ; elles vivent ici.
 */

import type { CanvasActionName } from '@hector/shared-types';
import { abortedBeforeMutation, toCanvasSnapshotSummary, type CanvasActionReceipt } from './actionReceipt';
import type { CanvasSnapshot } from './canvasSnapshot';

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** La chaîne, si elle porte autre chose que des blancs. */
export function chaine(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

/**
 * Les entrées d'une enveloppe de découverte du Document API, `{ items: T[] }`
 * (`sections.list`, `fields.list`, `query.match`). Le tableau nu couvre les doubles.
 */
export function entrees(source: unknown): Record<string, unknown>[] {
  const brut = Array.isArray(source) ? source : isRecord(source) && Array.isArray(source.items) ? source.items : [];
  return brut.filter(isRecord);
}

/** Longueur maximale d'un refus sans message, rendu en JSON au modèle. */
const MAX_REFUS_SANS_MESSAGE = 200;

/** Le refus du fournisseur, décomposé pour être rendu TEL QUEL au modèle. */
export function refusDuFournisseur(valeur: unknown): { code: string; message: string } {
  const failure = isRecord(valeur) ? valeur.failure : undefined;
  return {
    code: (isRecord(failure) ? chaine(failure.code) : undefined) ?? 'PROVIDER_REFUSAL',
    message: (isRecord(failure) ? chaine(failure.message) : undefined) ?? JSON.stringify(failure ?? valeur).slice(0, MAX_REFUS_SANS_MESSAGE),
  };
}

/** L'adresse d'une section. */
export type AdresseDeSection = { kind: 'section'; sectionId: string };

/** L'adresse de section publiée par `sections.list()`, ou `null` si illisible. */
export function adresseDeSection(entree: Record<string, unknown>): AdresseDeSection | null {
  const adresse = isRecord(entree.address) ? entree.address : entree;
  const sectionId = chaine(adresse.sectionId);
  return sectionId === undefined ? null : { kind: 'section', sectionId };
}

/**
 * Le refus de capacité : l'éditeur monté n'expose pas une surface que le verbe exige. Rien n'est écrit.
 *
 * @param refus.pourQuoi Ce que la surface sert à faire, quand le verbe en exige plusieurs (« pour régler les marges »).
 * @param refus.contournement Ce que le modèle fait à la place, à la suite du message.
 * @param refus.nextAction La suite, quand le verbe la dit hors du message.
 */
export function refusDeCapacite(refus: {
  intent: CanvasActionName;
  resume: CanvasSnapshot;
  surface: string;
  pourQuoi?: string;
  contournement?: string;
  nextAction?: string;
  recovery?: NonNullable<CanvasActionReceipt['recovery']>;
}): CanvasActionReceipt {
  return abortedBeforeMutation({
    intent: refus.intent,
    snapshot: toCanvasSnapshotSummary(refus.resume),
    code: 'CAPABILITY_UNAVAILABLE',
    message: `${refus.intent} exige editor.doc.${refus.surface}${refus.pourQuoi === undefined ? '' : ` ${refus.pourQuoi}`}, que cet éditeur n'expose pas. `
      + `Rien n'a été modifié et relancer la même action échouera à l'identique.${refus.contournement === undefined ? '' : ` ${refus.contournement}`}`,
    ...(refus.nextAction === undefined ? {} : { nextAction: refus.nextAction }),
    ...(refus.recovery === undefined ? {} : { recovery: refus.recovery }),
  });
}
